import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./Home.css";

/* ---- SVG Icons ---- */
const ArrowRight = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);
const CheckIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="var(--amber)"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const ProblemIcon = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    stroke="var(--amber)"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);
const HeartIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="var(--amber)"
    aria-hidden="true"
  >
    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
  </svg>
);
const LeafIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="var(--amber)"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M11 20A7 7 0 019.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10z" />
    <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" />
  </svg>
);
const BookIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="var(--amber)"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z" />
    <path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z" />
  </svg>
);
const UsersIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="var(--amber)"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 00-3-3.87" />
    <path d="M16 3.13a4 4 0 010 7.75" />
  </svg>
);

/* ---- CountUp ---- */
// Parses strings like "17+", "14,000+", "R1,200", "1 in 3", "27" and animates
// the numeric portion from 0 to its target value, preserving any prefix/suffix
// and the comma formatting. Non-numeric strings render unchanged.
function parseStatString(str) {
  const match = String(str).match(/^([^\d]*)([\d,]+)(.*)$/);
  if (!match) return null;
  const [, prefix, numberPart, suffix] = match;
  const target = parseInt(numberPart.replace(/,/g, ""), 10);
  if (isNaN(target)) return null;
  const hasCommas = numberPart.includes(",");
  return { prefix, target, suffix, hasCommas };
}

function formatNumber(value, hasCommas) {
  return hasCommas ? value.toLocaleString("en-US") : String(value);
}

function CountUp({ value, duration = 1500 }) {
  const parsed = parseStatString(value);
  const [display, setDisplay] = useState(parsed ? 0 : value);
  const ref = useRef(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (!parsed) return;
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated.current) {
            hasAnimated.current = true;
            const start = performance.now();
            const { target, hasCommas } = parsed;

            const step = (now) => {
              const elapsed = now - start;
              const progress = Math.min(elapsed / duration, 1);
              const eased = 1 - Math.pow(1 - progress, 3);
              const current = Math.round(eased * target);
              setDisplay(formatNumber(current, hasCommas));
              if (progress < 1) {
                requestAnimationFrame(step);
              } else {
                setDisplay(formatNumber(target, hasCommas));
              }
            };
            requestAnimationFrame(step);
          }
        });
      },
      { threshold: 0.3 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [parsed, duration]);

  if (!parsed) {
    return <span ref={ref}>{value}</span>;
  }

  return (
    <span ref={ref}>
      {parsed.prefix}
      {display}
      {parsed.suffix}
    </span>
  );
}

const stats = [
  { number: "17+", label: "Years of service" },
  { number: "250,000+", label: "Meals served" },
  { number: "4000+", label: "Literacy classes taught" },
  { number: "27", label: "Houses built for widows" },
  { number: "3", label: "Countries: Zambia, Malawi & SA" },
];

const impactAmounts = [
  { amount: "R100", impact: "Feeds a child for two full weeks" },
  { amount: "R500", impact: "Covers a full month of school fees" },
  { amount: "R550", impact: "Full set of skills training materials" },
  { amount: "R950", impact: "A food hamper for a family for the month" },
  {
    amount: "R1,000",
    impact: "Food hamper support for a family of five for a full month",
  },
  { amount: "R1,200", impact: "A full nutrition and psychosocial workshop" },
];

const pillars = [
  {
    icon: <HeartIcon />,
    title: "Nutrition & Health",
    desc: "We serve over 14,000 meals a year across our centres, run nutrition workshops for mothers, and provide home-based care to people living with HIV/AIDS.",
    link: "/what-we-do",
  },
  {
    icon: <BookIcon />,
    title: "Education",
    desc: "We fund school fees, uniforms and stationery, run Saturday school and literacy programmes, an ECD preschool, and support orphans and vulnerable children.",
    link: "/what-we-do",
  },
  {
    icon: <UsersIcon />,
    title: "Skills Development & Training",
    desc: "We run short courses and women's and youth empowerment programmes, equipping community members with practical skills and economic independence.",
    link: "/what-we-do",
  },
  {
    icon: <LeafIcon />,
    title: "Climate Change & the Environment",
    desc: "We are in the process of building a center in Lunga which will train people in sustainable farming practices and climate change.",
    link: "/what-we-do",
  },
];

const problems = [
  {
    stat: "1 in 3",
    desc: "Children in the communities we serve experience chronic hunger — arriving at school too hungry to learn.",
  },
  {
    stat: "45%",
    desc: "Of children in vulnerable communities are not enrolled in any form of early childhood education before Grade 1.",
  },
  {
    stat: "14,000+",
    desc: "Meals we serve every year — yet hunger remains a daily reality for thousands more across Southern Africa.",
  },
  {
    stat: "60%",
    desc: "Of people living with HIV/AIDS in poor communities cannot afford regular nutritious food to maintain their health.",
  },
];

const testimonials = [
  {
    quote:
      "My three youngest children go to the Center to collect breakfast they bring home and share with the others. This has helped me have peace of mind knowing my children have eaten whilst I'm away at the market as I only come back in the afternoon.",
    name: "Naomi Banda",
    role: "Feeding Programme Beneficiary",
  },
  {
    quote:
      "The Foundation built me a house. I no longer get worried when it rains. I am very happy and grateful to Food and Nutrition Foundation.",
    name: "Lianess",
    role: "Housing Beneficiary",
  },
];

export default function Home() {
  return (
    <main className="home-page">
      {/* ---- HERO ---- */}
      <section className="hero" aria-label="Welcome">
        <div
          className="hero-bg"
          style={{
            backgroundImage: "url('/images/gallery/smiling-children-03.jpg')",
          }}
        />
        <div className="hero-overlay" />
        <div className="container hero-content">
          <span className="eyebrow" style={{ color: "var(--amber-light)" }}>
            Bringing Real Hope · Since 2008
          </span>
          <h1 className="hero-title">
            Fighting Hunger
            <br />
            and Poverty.
          </h1>
          <p className="hero-subtitle">
            Your support empowers communities across South Africa, Malawi and
            Zambia with sustainable nutrition, education, and care.
          </p>
          <div className="hero-actions">
            <Link to="/donate" className="btn btn-primary btn-lg">
              Donate Now
            </Link>
            <Link to="/what-we-do" className="btn btn-secondary btn-lg">
              Learn More
            </Link>
          </div>
        </div>
      </section>

      {/* ---- IMPACT TICKER ---- */}
      <section className="impact-bar" aria-label="Key impact statistics">
        <div className="container">
          <div className="impact-bar-grid">
            {stats.map((s, i) => (
              <div className="impact-bar-item" key={i}>
                <span className="impact-bar-number">
                  <CountUp value={s.number} />
                </span>
                <span className="impact-bar-label">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---- THE PROBLEM ---- */}
      <section
        className="section problem-section"
        aria-labelledby="problem-heading"
      >
        <div className="container">
          <div className="section-title">
            <span className="eyebrow">The Reality on the Ground</span>
            <h2 id="problem-heading">The Problem Is Real — And It Is Now</h2>
            <div className="divider divider-center" />
            <p>
              Across Southern Africa, hunger, poverty, and illness do not wait
              for a convenient moment. They happen every single day — to
              children, to mothers, to the elderly, and to people already
              weakened by disease.
            </p>
          </div>
          <div className="grid-4 problem-grid">
            {problems.map((p, i) => (
              <div className="problem-card" key={i}>
                <div className="problem-stat">
                  <CountUp value={p.stat} />
                </div>
                <p className="problem-desc">{p.desc}</p>
              </div>
            ))}
          </div>
          <div className="problem-narrative">
            <div className="problem-narrative-content">
              <ProblemIcon />
              <div>
                <h3>What Happens Without Intervention</h3>
                <p>
                  When a child goes hungry, they cannot concentrate in school.
                  When a mother does not understand nutrition, her baby is more
                  likely to be stunted. When a person living with HIV/AIDS
                  cannot afford food, their medication stops working
                  effectively. When an orphan has no food or clothing, they are
                  excluded from school and community life.
                </p>
                <p>
                  These problems compound each other. Hunger makes poverty
                  worse. Poverty makes illness worse. Illness makes hunger
                  worse. The Food and Nutrition Foundation exists to break this
                  cycle — one meal, one lesson, one home, one family at a time.
                </p>
                <Link
                  to="/donate"
                  className="btn btn-primary"
                  style={{ marginTop: "0.5rem" }}
                >
                  Help Break the Cycle
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---- 4 PILLARS ---- */}
      <section
        className="section pillars-section"
        aria-labelledby="pillars-heading"
      >
        <div className="container">
          <div className="section-title">
            <span className="eyebrow">What We Do</span>
            <h2 id="pillars-heading">Four Pillars of Change</h2>
            <div className="divider divider-center" />
            <p>
              Every programme we run addresses a specific, documented need
              across Southern Africa.
            </p>
          </div>
          <div className="grid-4 pillars-grid">
            {pillars.map((p, i) => (
              <div className="pillar-card" key={i}>
                <div className="pillar-icon">{p.icon}</div>
                <h3 className="pillar-title">{p.title}</h3>
                <p className="pillar-desc">{p.desc}</p>
                <Link to={p.link} className="pillar-link">
                  Learn more <ArrowRight />
                </Link>
              </div>
            ))}
          </div>
          <div style={{ textAlign: "center", marginTop: "2.5rem" }}>
            <Link to="/what-we-do" className="btn btn-outline-green">
              See All Our Programmes
            </Link>
          </div>
        </div>
      </section>

      {/* ---- TESTIMONIAL ---- */}
      <section
        className="section testimonial-band"
        aria-labelledby="testimonials-heading"
      >
        <div className="container">
          <div className="section-title" style={{ color: "var(--white)" }}>
            <span className="eyebrow" style={{ color: "var(--amber-light)" }}>
              Voices From the Community
            </span>
            <h2 id="testimonials-heading" style={{ color: "var(--white)" }}>
              Their Words. Their Reality.
            </h2>
            <div className="divider divider-center" />
          </div>
          <div className="grid-2 testimonials-grid">
            {testimonials.map((t, i) => (
              <div className="testimonial testimonial-dark" key={i}>
                <blockquote>"{t.quote}"</blockquote>
                <p className="testimonial-author">
                  — {t.name}, <span style={{ fontWeight: 400 }}>{t.role}</span>
                </p>
              </div>
            ))}
          </div>
          <div style={{ textAlign: "center", marginTop: "2.5rem" }}>
            <Link to="/donate" className="btn btn-primary btn-lg">
              Be the Reason for the Next Story
            </Link>
          </div>
        </div>
      </section>

      {/* ---- YOUR DONATION DOES THIS ---- */}
      <section
        className="section impact-calc-section"
        aria-labelledby="impact-calc-heading"
      >
        <div className="container">
          <div className="section-title">
            <span className="eyebrow">Your Donation in Action</span>
            <h2 id="impact-calc-heading">What Your Gift Achieves</h2>
            <div className="divider divider-center" />
            <p>
              Every rand makes a tangible, traceable difference to a real person
              across our communities in Southern Africa.
            </p>
          </div>
          <div className="impact-calc-grid">
            {impactAmounts.map((item, i) => (
              <div className="impact-calc-card" key={i}>
                <div className="impact-amount">
                  <CountUp value={item.amount} />
                </div>
                <p className="impact-impact">{item.impact}</p>
              </div>
            ))}
          </div>
          <div style={{ textAlign: "center", marginTop: "3rem" }}>
            <Link to="/donate" className="btn btn-primary btn-lg">
              Choose Your Impact
            </Link>
          </div>
        </div>
      </section>

      {/* ---- APPROACH ---- */}
      <section
        className="section approach-section"
        aria-labelledby="approach-heading"
      >
        <div className="container">
          <div className="approach-grid">
            <div className="approach-image">
              <img
                src="/images/gallery/smiling-children-04.jpg"
                alt="Community members at a programme session"
                loading="lazy"
              />
            </div>
            <div className="approach-content">
              <span className="eyebrow">Our Approach</span>
              <h2 id="approach-heading">
                Community-Led. Evidence-Informed. Long-Term.
              </h2>
              <div className="divider" />
              <p>
                We do not parachute solutions into communities. We live in them.
                The Food and Nutrition Foundation was born in the communities we
                serve, founded by someone who saw the need firsthand and
                committed to addressing it from the inside out.
              </p>
              <p>
                Our approach recognises that hunger is a symptom — not the
                disease. The disease is systemic poverty, lack of education, and
                the compounding vulnerability created when illness, orphan
                status, and food insecurity collide. Our four pillars address
                these causes together, not in isolation.
              </p>
              <ul className="approach-list">
                {[
                  "We meet people where they are — in their homes, schools, and communities",
                  "We build skills and knowledge, not just dependency on food parcels",
                  "We construct lasting infrastructure: homes, classrooms, support systems",
                  "We prioritise dignity in every interaction, every visit, every meal",
                ].map((item, i) => (
                  <li key={i}>
                    <CheckIcon />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/about"
                className="btn btn-outline-green"
                style={{ marginTop: "1.5rem" }}
              >
                Our Full Story
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ---- FOUNDER QUOTE ---- */}
      <section
        className="section founder-section"
        aria-label="Founder's message"
      >
        <div className="container">
          <div className="founder-card">
            <div className="founder-image">
              <img
                src="/images/charity-katotobwe.jpg"
                alt="Ms. Charity Katotobwe, Founder"
                loading="lazy"
              />
            </div>
            <div className="founder-content">
              <span className="eyebrow">A Word From Our Founder</span>
              <blockquote className="founder-quote">
                "Everyone is entitled to a dignified existence irrespective of
                socio-economic status. For 17 years we have shown up, cooked the
                pot, built the houses, and educated the children — because we
                believe no one should be left behind."
              </blockquote>
              <p className="founder-name">Ms. Charity Katotobwe</p>
              <p className="founder-title-text">
                Founder &amp; Executive Director, Food and Nutrition Foundation
              </p>
              <p className="founder-sub">
                ACCA · MBA · PhD Candidate in Entrepreneurship · Founded 2005 ·
                Operating since 2008
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ---- CTA BAND ---- */}
      <section className="cta-band" aria-label="Donation call to action">
        <div className="container cta-band-inner">
          <div>
            <h2>Ready to Change a Life?</h2>
            <p>
              Your donation today feeds a child, educates a mother, and builds a
              stronger community across Southern Africa.
            </p>
          </div>
          <div className="cta-band-actions">
            <Link to="/donate" className="btn btn-primary btn-lg">
              Donate Now
            </Link>
            <Link to="/get-involved" className="btn btn-secondary">
              Volunteer Instead
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
